import type { InboundOnlyTransport } from "@vidimo/shared";
import {
  type GuestMessageTransport,
  GuestMessageTransportSchema,
  guestMessageTransportValues,
} from "@vidimo/shared/enums";
import {
  type PlatformId,
  platformDisplayName,
  platformStatus,
} from "@vidimo/shared/platforms";
import type { useTranslations } from "use-intl";
import type { LabelsTranslator } from "@/lib/utils";

/** Each platform's key under labels.transport, or null for a brand. */
const PLATFORM_CATALOG_KEY: {
  [P in PlatformId]: P extends "email" | "phone" ? P : null;
} = {
  airbnb: null,
  booking_com: null,
  email: "email",
  instagram: null,
  phone: "phone",
  vrbo: null,
  whatsapp: null,
};

/**
 * What a host calls a channel: a brand by its own name, email, phone and SMS
 * in the host's language, since the registry's word for those is English.
 * Only this module calls platformDisplayName, which a guard holds.
 */
export function transportLabel(
  tLabels: LabelsTranslator,
  channel: PlatformId | "sms"
): string {
  if (channel === "sms") {
    return tLabels("transport.sms");
  }
  const key = PLATFORM_CATALOG_KEY[channel];
  return key ? tLabels(`transport.${key}`) : platformDisplayName(channel);
}

/**
 * Transports whose identifier IS a column on `guests`. Never trusted from a
 * `guest_identities` row even when one claims them: the canonical identifier
 * lives on the guest row, so a stray row must not grant reachability.
 */
const PROFILE_DERIVED = new Set<GuestMessageTransport>([
  "sms",
  "whatsapp",
  "email",
]);

export interface TransportDeliveryContext {
  /**
   * True when the guest email channel is configured server-side,
   * from GET /public/channel-features. Undefined is treated as NOT
   * configured: the server fail-closes the send, so assuming otherwise would
   * offer a dead channel.
   */
  emailChannelConfigured?: boolean;
  /** As `emailChannelConfigured`, for Instagram; undefined is NOT configured. */
  instagramChannelConfigured?: boolean;
  /**
   * True when the property has an active Twilio `phone` integration carrying a
   * number. That is exactly what `sendTwilioMessage` resolves before an SMS
   * send, so gating on it withholds precisely the sends that would throw.
   */
  smsSenderConnected?: boolean;
  /** Same, for the property's active Twilio `whatsapp` integration. */
  whatsAppSenderConnected?: boolean;
  /**
   * True when a WhatsApp sender exists for the property but has not finished
   * connecting. Mid-onboarding is a different sentence from never-started: the
   * Messaging card calls this state "Setting up" and tells the host it
   * finishes on its own, so the picker must not send them back to hunt for a
   * Connect button they already pressed.
   */
  whatsAppSenderPending?: boolean;
}

/** Why a transport can't deliver right now; each code is a catalog key. */
type TransportWithheldReason =
  | "emailNotConfigured"
  | "inboundOnly"
  | "instagramNotSetUp"
  | "smsNotConnected"
  | "smsUnavailable"
  | "whatsAppNotConnected"
  | "whatsAppSettingUp";

/**
 * While `phone:twilio` is `planned` the Messaging screen shows a disabled
 * "Coming soon" card, so telling the host no number is connected would send
 * them hunting for a connect button that screen deliberately doesn't offer.
 * Reading the matrix means this corrects itself the day phone ships.
 */
function smsWithheldReason(): TransportWithheldReason {
  return platformStatus("phone") === "planned"
    ? "smsUnavailable"
    : "smsNotConnected";
}

/**
 * What stands between each transport and a delivered message, or null when
 * nothing does. A `Record` over the enum, so a new transport fails to compile
 * until it declares its gate.
 *
 * Booking.com and Airbnb are inbound-only: their messages arrive and render,
 * and the dispatcher refuses the reply, so no context makes them send targets.
 *
 * This is not the server's enforcement: each dispatcher re-checks at egress.
 * Nor does it know `TWILIO_OUTBOUND_ENABLED`, the kill switch that mutes real
 * sends outside production; a picker that withheld every channel there would
 * leave nothing to demo or develop against.
 */
const GATES: Record<
  GuestMessageTransport,
  (ctx: TransportDeliveryContext) => TransportWithheldReason | null
> = {
  airbnb: () => "inboundOnly",
  booking_com: () => "inboundOnly",
  email: (ctx) => (ctx.emailChannelConfigured ? null : "emailNotConfigured"),
  instagram: (ctx) =>
    ctx.instagramChannelConfigured ? null : "instagramNotSetUp",
  sms: (ctx) => (ctx.smsSenderConnected ? null : smsWithheldReason()),
  whatsapp: (ctx) => {
    if (ctx.whatsAppSenderConnected) {
      return null;
    }
    return ctx.whatsAppSenderPending
      ? "whatsAppSettingUp"
      : "whatsAppNotConnected";
  },
};

/** Why `transport` can't deliver for this property, or null when it can. */
export function transportWithheldReason(
  transport: GuestMessageTransport,
  ctx: TransportDeliveryContext = {}
): TransportWithheldReason | null {
  return GATES[transport](ctx);
}

/** One row of the composer's transport picker. */
export interface GuestTransportOption {
  /** Why this channel can't carry a message right now, or null when it can. */
  reason: TransportWithheldReason | null;
  transport: GuestMessageTransport;
}

type GuestTransportsTranslator = ReturnType<
  typeof useTranslations<"communications.guestTransports">
>;

/** The host's sentence for a withheld channel. */
export function transportWithheldMessage(
  t: GuestTransportsTranslator,
  transport: GuestMessageTransport,
  reason: TransportWithheldReason
): string {
  if (reason !== "inboundOnly") {
    return t(reason);
  }
  // Only a booking platform's gate withholds a transport as inbound-only.
  const platform = platformDisplayName(transport as InboundOnlyTransport);
  return t("inboundOnly", { platform });
}

/**
 * Derive the composer's transport picker for a guest: which channels reach
 * them, and which of those can actually deliver right now.
 *
 * Two questions, answered in two places.
 *
 * **Reachable** — does this guest have an address on the channel? Derived
 * here, from the guest's profile columns plus any recorded `guest_identities`
 * rows:
 *
 * 1. **Profile-derived** — the identifier IS a column on `guests`, so
 *    there's no separate identity to record. Storing them as rows in
 *    `guest_identities` would just duplicate the column.
 *      - `guest.phone` → `sms`, `whatsapp` (both keyed on the same E.164
 *        number; we optimistically assume any number is on WhatsApp — the
 *        actual send will fail at the Twilio layer if not, but that's a
 *        delivery-time concern, not a reachability concern).
 *      - `guest.email` → `email`.
 *
 * 2. **Identity-recorded** — the identifier is genuinely external and has
 *    no column on `guests`, so it lives on `guest_identities.externalId`.
 *    We surface those by inspecting the rows.
 *      - `booking_com` / `airbnb`: a thread id assigned at booking time.
 *      - `instagram`: the guest's IGSID, which only exists once they have
 *        DMed the property (the channel is reactive-only).
 *
 * **Deliverable** — can this property send on the channel at all? Answered by
 * `transportWithheldReason`. Withheld channels stay in the list carrying their
 * reason; the picker renders them disabled rather than dropping them.
 *
 * Unknown / unrecognised transport values on identity rows are silently
 * dropped (forward-compatibility: a new platform enum value shipped to the
 * server before clients know about it shouldn't crash the chat input).
 *
 * Order follows `guestMessageTransportValues` — the picker's display order.
 */
export function deriveGuestTransports(
  guest: { phone?: string | null; email?: string | null } | null | undefined,
  identityRows: ReadonlyArray<{ transport: string | null | undefined }>,
  delivery: TransportDeliveryContext = {}
): GuestTransportOption[] {
  const reachable = new Set<GuestMessageTransport>();

  if (guest?.phone) {
    reachable.add("whatsapp");
    reachable.add("sms");
  }
  if (guest?.email) {
    reachable.add("email");
  }

  // Only externally-keyed transports come from identity rows. An identity row
  // IS the reachability signal for those — an Instagram IGSID or an OTA thread
  // id exists only because the guest opened the thread, and there is no
  // proactive path to offer without one.
  for (const row of identityRows) {
    const parsed = GuestMessageTransportSchema.safeParse(row.transport);
    if (parsed.success && !PROFILE_DERIVED.has(parsed.data)) {
      reachable.add(parsed.data);
    }
  }

  // Stable display order — the canonical transport order the picker renders,
  // shared so a new transport can't drift out of a hand-maintained copy.
  return guestMessageTransportValues
    .filter((transport) => reachable.has(transport))
    .map((transport) => ({
      reason: transportWithheldReason(transport, delivery),
      transport,
    }));
}

/** The subset a host can actually send on — the picker's enabled entries. */
export function deliverableTransports(
  options: readonly GuestTransportOption[]
): GuestMessageTransport[] {
  return options
    .filter((option) => option.reason === null)
    .map((option) => option.transport);
}
